import React from 'react';
import { get, merge, cloneDeep, filter, findIndex, find } from 'lodash';

// local dependencies
import { SERVICE_RESPONSE_STATUS, PAGE_LEVEL_PRELOADER_TEMPLATES } from 'constants/common';
import { DrugPricingPharmacyLocatorService } from 'services/drugPricingPharmacyLocator';
import { drugPricingPharmacyLocator as DrugPricingPharmacyLocatorView } from 'ui/views/widgets/drugPricingPharmacyLocator';
import * as errorOverlay from 'utils/errorOverlay';
import * as pageLevelPreloader from 'modules/page-level-preloader';
import { focusLocation } from 'modules/googleMap';

/**
 * @type {class}
 * @desc A React container/smart component to render a React presentational/dumb component (view).
 * @example
 * import { DrugPricingPharmacyLocator } from 'ui/containers/DrugPricingPharmacyLocator';
 *
 * // render
 * <DrugPricingPharmacyLocator propName='propValue'/>
 */
export class DrugPricingPharmacyLocator extends React.Component {
    
    /**
     * @param {object} props - props
     */
    constructor( props ) {
        super( props );

        // component state
        this.state = {
            isFetching: true,
            networkStatus: undefined,
            componentData: undefined,
            filters: {},

            // locations state
            isFetchingLocations: false,
            currentPage: 1,
            locationsPerPage: 5, // 5 locations per page
            selectedLocation: null,
            mapId: 'drugSearchPharmacies',

            requestedDrugPricingId: []
        };

        // create service instance
        this.drugPricingPharmacyLocatorService = new DrugPricingPharmacyLocatorService();
        
        // start data fetch
        this.fetchComponentData();

        // bind
        this.__bind();

    }

    /**
     * @desc bind events and methods
     */
    __bind() {
        log.debug( 'DrugPricingPharmacyLocator.__bind()' );

        this.handleActivateLocationsPage = this.handleActivateLocationsPage.bind( this );
        this.handleLocationSelect = this.handleLocationSelect.bind( this );
        this.handleFiltersChange = this.handleFiltersChange.bind( this );
        this.fetchDrugPrice = this.fetchDrugPrice.bind( this );
    }

    /*********************************************/

    /**
     * @desc Fetch component response.
     */
    async fetchComponentData() {
        log.debug( 'DrugPricingPharmacyLocator.fetchComponentData()' );

        // cancel ongoing request
        this.drugPricingPharmacyLocatorService.cancel();

        // fetch component data
        const { status, data } = await this.drugPricingPharmacyLocatorService.fetchComponentData( {
            url: this.props.widget.url,
            data: this.props.requestPayload,
        } );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {
            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.FAILURE
            } );
        } else {

            this.setState( {
                isFetching: false,
                networkStatus: SERVICE_RESPONSE_STATUS.SUCCESS,
                componentData: data,
                filters: get( data, 'filters', {} ),
            } );
        }
    }

    /**
     * @desc Switch current activated locations page
     */
    handleActivateLocationsPage( pageNumber ) {
        log.debug( 'DrugPricingPharmacyLocator.handleActivateLocationsPage()' );

        this.setState( {
            currentPage: pageNumber,
            selectedLocation: null,
        } );
    }

    /**
     * @desc When pharmacy location is clicked
     */
    handleLocationSelect( locationId ) {
        log.debug( 'DrugPricingPharmacyLocator.handleLocationSelect()' );

        this.setState( {
            selectedLocation: locationId,
        }, () => {
            focusLocation( this.state.mapId, locationId );
        } );
    }

    /**
     * @desc When filter changes, reload widget data
     */
    handleFiltersChange( filters = {} ) {
        log.debug( 'DrugPricingPharmacyLocator.handleFiltersChange()' );

        this.setState( {
            filters: filters,
            isFetchingLocations: true,
        }, () => {
            this.fetchPharmacies( {
                data: { location: filters },
            } );
        } );
    }

    /**
     * @desc Fetch pharmacies based on new filers.
     */
    async fetchPharmacies( requestPayload = {} ) {
        log.debug( 'DrugPricingPharmacyLocator.fetchPharmacies()' );

        // cancel ongoing request
        this.drugPricingPharmacyLocatorService.cancel();

        // show page level preloader
        pageLevelPreloader.show( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // fetch component data
        const { status, data } = await this.drugPricingPharmacyLocatorService.fetchPharmacies( {
            url: this.props.widget.url,
            data: merge( requestPayload, this.props.requestPayload ),
        } );

        // hide page level preloader
        pageLevelPreloader.hide( PAGE_LEVEL_PRELOADER_TEMPLATES.NANOBAR );

        // check if response status was success
        if( SERVICE_RESPONSE_STATUS.CANCELLED === status ) {
           return null; // ignore on request cancellations
        } else if( SERVICE_RESPONSE_STATUS.FAILURE === status ) {
            errorOverlay.open(); // open error overlay
        } else {

            // update state
            this.setState( {
                isFetchingLocations: false,
                currentPage: 1, // set page selection
                componentData: {
                    ...this.state.componentData,
                    retailPharmacies: get( data, 'retailPharmacies', [] ),
                }
            } );
        }
    }

    fetchDrugPrice( drugInfo ) {
        log.debug( 'DrugPricingPharmacyLocator.fetchDrugPrice()' );

        const requestedDrugPricingId =  cloneDeep( this.state.requestedDrugPricingId );

        this.setState( { requestedDrugPricingId: [ ...requestedDrugPricingId, drugInfo.id ] }, async () => {

            // cancel ongoing request
            this.drugPricingPharmacyLocatorService.cancel();

            const componentData = cloneDeep( this.state.componentData );
            const retailPharmaciesAction = get( componentData, 'locationsConfig.retailPharmacies.actions' );
            const fetchDrugPriceAction = find( retailPharmaciesAction, { name: 'fetchDrugPrice' } );

             // fetch component data
            const { status, data } = await this.drugPricingPharmacyLocatorService.fetchDrugPrice( {
                url: fetchDrugPriceAction.url,
                data: merge( { data: { id: drugInfo.id } }, this.props.requestPayload ),
            } );

            const _requestedDrugPricingId = filter( this.state.requestedDrugPricingId, id => {
                return id !== drugInfo.id;
            } );

            // check if response status was success
            if( SERVICE_RESPONSE_STATUS.SUCCESS !== status ) {

                errorOverlay.open(); // open error overlay

                // update state
                this.setState( {
                    requestedDrugPricingId: _requestedDrugPricingId,
                } );

            } else {

                const retailPharmaciesIndex = findIndex( componentData.retailPharmacies, { id: drugInfo.id } );
                componentData.retailPharmacies[ retailPharmaciesIndex ] = {
                    ...componentData.retailPharmacies[ retailPharmaciesIndex ],
                    ...data
                };
                
                // update state
                this.setState( {
                    requestedDrugPricingId: _requestedDrugPricingId,
                    componentData: componentData
                } );
            }

        } );
    }

    /*********************************************/

    /**
     * @desc When component unmounts, cleanup resouces.
     */
    componentWillUnmount() {
        this.drugPricingPharmacyLocatorService.cancel(); // cancel ongoing requests
    }


    /**
     * @desc Render a React view component
     */
    render() {
        return (
            <DrugPricingPharmacyLocatorView
                isFetching={ this.state.isFetching }
                networkStatus={ this.state.networkStatus }
                { ...get( this.state, 'componentData', {} ) }
                mapId={ this.state.mapId }
                currentPage={ this.state.currentPage }
                locationsPerPage={ this.state.locationsPerPage }
                selectedLocation={ this.state.selectedLocation }
                isFetchingLocations={ this.state.isFetchingLocations }
                onActivateLocationsPage={ this.handleActivateLocationsPage }
                onLocationSelect={ this.handleLocationSelect }
                onFiltersChange={ this.handleFiltersChange }
                filters={ this.state.filters }
                fetchDrugPrice={ this.fetchDrugPrice }
                requestedDrugPricingId={ this.state.requestedDrugPricingId }
            />
        );
    }
}

// set display name
DrugPricingPharmacyLocator.displayName = 'DrugPricingPharmacyLocator';

// set default props
DrugPricingPharmacyLocator.defaultProps = {
    requestPayload: {},
};

// prop types
DrugPricingPharmacyLocator.propTypes = {};
